import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useReprocessPreview } from "@/hooks/use-sources";

interface ProcessSourceConfirmDialogProps {
  sourceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isProcessing: boolean;
}

/**
 * Reprocess-confirmation modal. Fetches the live preview counts when open
 * so the user knows exactly what they'd lose and what they'd keep.
 */
export function ProcessSourceConfirmDialog({
  sourceId,
  open,
  onOpenChange,
  onConfirm,
  isProcessing,
}: ProcessSourceConfirmDialogProps) {
  const { data, isLoading, isError } = useReprocessPreview(sourceId, open);

  const description = (() => {
    if (isLoading) return "Loading…";
    if (isError || !data) return "Could not load reprocess preview. Try again in a moment.";
    return `Reprocessing will regenerate ${data.untouchedCount} untouched item${data.untouchedCount === 1 ? "" : "s"}. ${data.decidedCount} item${data.decidedCount === 1 ? "" : "s"} with your triage decisions and ${data.viewedCount} item${data.viewedCount === 1 ? "" : "s"} you've viewed will be preserved.`;
  })();

  const continueDisabled = isLoading || isError || !data || isProcessing;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reprocess this source?</AlertDialogTitle>
          <AlertDialogDescription>
            {isLoading ? <Skeleton className="h-4 w-3/4" /> : description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={continueDisabled}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {isProcessing ? "Processing…" : "Reprocess"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
